import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import LoginForm from './LoginForm';
import AdminPanel from './AdminPanel';
import BasePage from './BasePage';
import TenantsPage from './TenantsPage';
import VersionPage from './VersionPage';
import ManagerPanel from './ManagerPanel';
import UserPanel from './UserPanel';
import UserLogsPage from './UserLogsPage';
import UserDashboard from './userDashboard';
import CookieConsentPage from './CookieConsentPage';
import CookiesNotifyPage from './CookiesNotifyPage';
import ConsentOverviewPage from './ConsentOverviewPage';
import SmartConsent from './SmartConsent';
import DSARDashboard from './DSARDashboard';
import DSARDashboardUser from './DSARDashboardUser';
import CustomReport from './CustomReport';
import NoticesPage from './NoticesPage';
import SmartNotices from './SmartNotices';
import WebAppConsent from './WebAppConsent';
import TermsAndConditions from './terms and conditions';
import PurposeConsentPage from './PurposeConsentPage';
import CustomerConsentLogin from './customerConsentLogin';
import CustomerConsent from './customerConsent';
import DpdpConsentPage from './ConsentBannerPage';
import ConsentBannerNofity from './ConsentBannerNofity';
import MarketConsentPage from './MarketConsentPage';
import DummyConsentPage from './DummyConsentPage';
import '../css/App.css';
import LoadingOverlay from './LoadingOverlay';
import axios from './axiosSetup';
import API_BASE from './apiBase';
import LocalSsoLogin from './LocalSsoLogin';

const normalizePath = (pathname = '') => {
  // return pathname.split('?')[0].split('#')[0] || '/';
  const path = (pathname.split('?')[0].split('#')[0] || '/').trim();
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path || '/';
};

const normalizeUser = (value) => {
  if (!value || typeof value !== 'object') return value;
  return value.role === 'super_admin' ? { ...value, role: 'admin' } : value;
};

const getUserPageFromPath = (pathname) => {
  switch (normalizePath(pathname)) {
    case '/': return 'consentoverview';
    case '/user': return 'consentoverview';
    case '/user/dashboard': return 'dashboard';
    case '/user/consent-overview': return 'consentoverview';
    case '/user/smart-consent': return 'smart-consent';
    case '/user/smartconsent': return 'smart-consent';
    case '/user/consent': return 'consent';
    case '/user/cookies-notify': return 'cookies-notify';
    case '/user/consent-banner': return 'consent-banner';
    case '/user/consentBannerNofity': return 'consent-banner';
    case '/user/custom-report': return 'custom-report';
    case '/user/dps': return 'dps';
    case '/user/notices': return 'notices';
    case '/user/smart-notices': return 'smart-notices';
    case '/user/smartnotices': return 'smart-notices';
    case '/user/purpose-consent': return 'purpose-consent';
    case '/user/purpose%20consent': return 'purpose-consent';
    case '/user/purpose consent': return 'purpose-consent';
    case '/user/market-consent': return 'market-consent';
    case '/user/logs': return 'user-logs';
    case '/user/user-logs': return 'user-logs';
    case '/version': return 'version';
    default: return null;
  }
};

const getUserPathFromPage = (page) => {
  switch (page) {
    case 'dashboard': return '/user/dashboard';
    case 'consentoverview': return '/user/consent-overview';
    case 'smart-consent': return '/user/smart-consent';
    case 'smart-notices': return '/user/smart-notices';
    case 'consent': return '/user/consent';
    case 'market-consent': return '/user/market-consent';
    case 'cookies-notify': return '/user/cookies-notify';
    case 'consent-banner': return '/user/consent-banner';
    case 'custom-report': return '/user/custom-report';
    case 'dps': return '/user/dps';
    case 'notices': return '/user/notices';
    case 'purpose-consent': return '/user/purpose-consent';
    case 'purpose consent': return '/user/purpose-consent';
    case 'user-logs': return '/user/logs';
    case 'version': return '/version';
    default: return '/user/dashboard';
  }
};

const getInitialPage = () => {
  const pathname = window.location.pathname;
  let storedUser = null;
  try {
    const raw = localStorage.getItem('user');
    // if (raw) storedUser = JSON.parse(raw);
    if (raw) storedUser = normalizeUser(JSON.parse(raw));
  } catch (e) {
    storedUser = null;
  }

  if (storedUser && storedUser.role === 'manager') {
    return getManagerPageFromPath(pathname);
  }
  if (storedUser && storedUser.role === 'admin') {
    return getAdminPageFromPath(pathname);
  }
  return getUserPageFromPath(pathname);
};

const getSafeLandingPath = (role) => {
  if (role === 'manager') return '/manager';
  if (role === 'admin') return '/admin/tenants';
  return '/user/consent-overview';
};

const getManagerPageFromPath = (pathname) => {
  switch (normalizePath(pathname)) {
    case '/': return 'dashboard';
    case '/manager': return 'dashboard';
    case '/manager/dashboard': return 'dashboard';
    case '/manager/users': return 'users';
    case '/manager/consent': return 'consent';
    case '/manager/cookie-consent': return 'cookie-consent-template';
    case '/manager/notification': return 'notification';
    case '/manager/Data_Types': return 'Data_Types';
    case '/manager/DSAR': return 'DSAR';
    case '/manager/Department': return 'Department';
    case '/manager/templates': return 'templates';
    case '/manager/consent-banner': return 'consent-banner-template';
    case '/manager/market-consent': return 'market-consent';
    default: return null;
  }
};

const getManagerPathFromPage = (page) => {
  switch (page) {
    case 'dashboard': return '/manager';
    case 'users': return '/manager/users';
    case 'consent': return '/manager/consent';
    case 'market-consent': return '/manager/market-consent';
    case 'cookie-consent-template': return '/manager/cookie-consent';
    case 'consent-banner-template': return '/manager/consent-banner';
    case 'notification': return '/manager/notification';
    case 'Data_Types': return '/manager/Data_Types';
    case 'DSAR': return '/manager/DSAR';
    case 'Department': return '/manager/Department';
    case 'templates': return '/manager/templates';
    default: return '/manager';
  }
};

const getAdminPageFromPath = (pathname) => {
  switch (normalizePath(pathname)) {
    case '/': return 'tenants';
    case '/admin': return 'tenants';
    case '/admin/tenants': return 'tenants';
    case '/admin/cookie-consent': return 'cookie-consent-template';
    case '/dummy_page': return 'dummy-consent';
    case '/admin/dummy_page': return 'dummy-consent';
    case '/admin/dummy-consent': return 'dummy-consent';
    default: return null;
  }
};

const NotFoundPage = ({ label = 'Page not found', onHome }) => (
  <div className="not-found-page" style={{ padding: '40px', textAlign: 'center' }}>
    <h1 style={{ fontSize: '4rem', marginBottom: '16px' }}>404</h1>
    <p style={{ fontSize: '1.25rem', marginBottom: '24px' }}>{label}</p>
    {onHome && (
      <button
        type="button"
        style={{ padding: '12px 24px', fontSize: '1rem', cursor: 'pointer' }}
        onClick={onHome}
      >
        Go to Home
      </button>
    )}
  </div>
);

const getAdminPathFromPage = (page) => {
  switch (page) {
    case 'tenants': return '/admin/tenants';
    case 'cookie-consent-template': return '/admin/cookie-consent';
    case 'dummy-consent': return '/dummy_page';
    default: return '/admin/tenants';
  }
};

function App() {
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem('user');
      // return u ? JSON.parse(u) : null;
      return u ? normalizeUser(JSON.parse(u)) : null;
    } catch (e) {
      return null;
    }
  });
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token') || '';
    } catch (e) {
      return '';
    }
  });
  // const [page, setPage] = useState('tenants');
  // conset [managerPage, setManagerPage] = useState('dashboard');
  const [page, setPage] = useState(getInitialPage);
  const [managerPage, setManagerPage] = useState(() => getManagerPageFromPath(window.location.pathname));

  // Check special standalone pages by path
  const normalizedLocation = normalizePath(window.location.pathname);
  const isWebAppConsentPage = normalizedLocation === '/webapp-consent';
  const isTermsAndConditionsPage = normalizedLocation === '/terms-and-conditions' || normalizedLocation === '/terms';
  const isCustomerConsentLoginPage = normalizedLocation === '/customer-consent-login';
  const isCustomerConsentPage = normalizedLocation === '/customer-consent';
  const isDSARDashboardUserPage = normalizedLocation === '/dsar-dashboard';
  const isLocalSsoLoginPage = normalizedLocation === '/local-sso-login';

  // Show payment result alerts when Stripe redirects back to /billing/success or /billing/cancel
  useEffect(() => {
    try {
      const p = window.location.pathname || '';
      // Only show once per session redirect
      const key = '__billing_alert_shown__';
      if (sessionStorage.getItem(key)) return;
      if (p === '/billing/success') {
        sessionStorage.setItem(key, '1');
        Swal.fire({ icon: 'success', title: 'Payment Successful', text: 'Thank you — your payment was successful.' }).then(() => {
          try { window.history.replaceState({}, '', '/'); } catch (e) { }
        });
      } else if (p === '/billing/cancel') {
        sessionStorage.setItem(key, '1');
        Swal.fire({ icon: 'warning', title: 'Payment Cancelled', text: 'Payment was cancelled. No changes were made.' }).then(() => {
          try { window.history.replaceState({}, '', '/'); } catch (e) { }
        });
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const tk = localStorage.getItem('token');
    if (tk) {
      // axios.get(`${API_BASE}/user/status`, { timeout: 5000 })
      axios.get(`${API_BASE}/user/status`, { timeout: 5000, authCheck: true })
        .then(res => {
          if (res.data && res.data.success && res.data.user) {
            // setUser(res.data.user);
            // localStorage.setItem('user', JSON.stringify(res.data.user));
            const restoredUser = normalizeUser(res.data.user);
            setUser(restoredUser);
            localStorage.setItem('user', JSON.stringify(restoredUser));
            const tenantId = restoredUser?.tenant_id || restoredUser?.tenantId || restoredUser?.tenant;
            if (tenantId) localStorage.setItem('tenant_id', String(tenantId));
          }
        })
        .catch(err => {
          console.warn('App: session verify failed:', err && err.message);
        });
    }
  }, []);
  const [selectedConsentOverview, setSelectedConsentOverview] = useState(() => {
    try {
      const raw = sessionStorage.getItem('selectedConsentOverview');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (selectedConsentOverview) {
        sessionStorage.setItem('selectedConsentOverview', JSON.stringify(selectedConsentOverview));
      } else {
        sessionStorage.removeItem('selectedConsentOverview');
      }
    } catch (e) {
      // ignore storage issues
    }
  }, [selectedConsentOverview]);

  const handleUserMenu = React.useCallback((menuPage) => {
    setPage(menuPage);
    try {
      const nextPath = getUserPathFromPage(menuPage);
      if (nextPath && window.location.pathname !== nextPath) {
        window.history.pushState({}, '', nextPath);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (user && user.role === 'user') {
        setPage(getUserPageFromPath(window.location.pathname));
      } else if (user && user.role === 'manager') {
        setManagerPage(getManagerPageFromPath(window.location.pathname));
      } else if (user && user.role === 'admin') {
        setPage(getAdminPageFromPath(window.location.pathname));
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [user]);

  const handleLogin = (data) => {
    console.log('app handleLogin', data);
    // setUser(data.user);
    const normalizedUser = normalizeUser(data.user);
    const normalizedData = { ...data, user: normalizedUser };
    setUser(normalizedUser);
    setToken(data.token);
    try {
      // localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      localStorage.setItem('token', data.token || '');
      // Record login timestamp so we can auto-logout after 24 hours.
      try { localStorage.setItem('login_at', String(Date.now())); } catch (e) { }
      // Persist tenant_id globally so other pages can pick it up immediately
      try {
        // const tid = normalizedUser && (data.user.tenant_id || data.user.tenantId || data.user.tenant) ? (data.user.tenant_id || data.user.tenantId || data.user.tenant) : null;
        const tid = normalizedUser && (normalizedUser.tenant_id || normalizedUser.tenantId || normalizedUser.tenant) ? (normalizedUser.tenant_id || normalizedUser.tenantId || normalizedUser.tenant) : null;
        if (tid) {
          localStorage.setItem('tenant_id', String(tid));
          try { if (!window.USER) window.USER = {}; window.USER.tenant_id = Number(tid); } catch (e) { }
          try { if (!window.__USER__) window.__USER__ = {}; window.__USER__.tenant_id = Number(tid); } catch (e) { }
          try { window.dispatchEvent(new CustomEvent('tenantChanged', { detail: { tenant_id: Number(tid) } })); } catch (e) { }
        }
      } catch (e) { }
      try {
        // if (data.user && data.user.role === 'user') {
        // const role = data.user && data.user.role;
        const role = normalizedData.user && normalizedData.user.role;
        if (role === 'user') {
          const nextPage = getUserPageFromPath(window.location.pathname);
          const effectivePage = nextPage === 'dashboard' ? 'consentoverview' : nextPage;
          setPage(effectivePage);
          const nextPath = effectivePage === 'dashboard'
            ? '/user/consent-overview'
            : effectivePage === null
              // ? window.location.pathname
              ? getSafeLandingPath(role)
              : getUserPathFromPage(effectivePage);
          try { window.history.replaceState({}, '', nextPath); } catch (e) { }
        } else if (role === 'manager') {
          setManagerPage('dashboard');
          try { window.history.replaceState({}, '', getSafeLandingPath(role)); } catch (e) { }
        } else if (role === 'admin') {
          setPage('tenants');
          try { window.history.replaceState({}, '', getSafeLandingPath(role)); } catch (e) { }
        }
      } catch (e) { }
    } catch (e) {
      // ignore
    }
  };

  // const handleLogout = () => {
  const handleLogout = React.useCallback(() => {
    setUser(null);
    setToken('');
    setPage('dashboard');
    setManagerPage('dashboard');
    setSelectedConsentOverview(null);
    try {
      sessionStorage.removeItem('selectedConsentOverview');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('login_at');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('last_active_at');
      localStorage.setItem('session_logout', JSON.stringify({ reason: 'manual', timestamp: Date.now() }));
      try { if (window.__USER__) delete window.__USER__; } catch (e) { }
      try { if (window.USER) delete window.USER; } catch (e) { }
    } catch (e) {
      // ignore
    }

    try {
      window.history.replaceState({}, '', '/');
    } catch (e) { }

    try {
      window.location.replace('/');
    } catch (e) { }
  }, [setUser, setToken, setPage, setManagerPage, setSelectedConsentOverview]);


  React.useEffect(() => {
    if (!user) return undefined;
    const SESSION_MS = 24 * 60 * 60 * 1000; // 24 hours

    const getExpiryMs = () => {
      // 1) Try JWT exp claim
      try {
        const tk = localStorage.getItem('token') || '';
        if (tk && tk.split('.').length === 3) {
          const payloadStr = atob(tk.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'));
          const payload = JSON.parse(payloadStr);
          if (payload && payload.exp) {
            return Number(payload.exp) * 1000; // exp is in seconds
          }
        }
      } catch (e) { }
      // 2) Fallback to login_at + 24h
      try {
        const loginAt = Number(localStorage.getItem('login_at') || 0);
        if (loginAt) return loginAt + SESSION_MS;
      } catch (e) { }
      // 3) No info — start a fresh window from now
      const now = Date.now();
      try { localStorage.setItem('login_at', String(now)); } catch (e) { }
      return now + SESSION_MS;
    };

    const expiryMs = getExpiryMs();
    const remaining = expiryMs - Date.now();

    if (remaining <= 0) {
      // Already expired — log out immediately
      try { window.alert('Your session has expired. Please log in again.'); } catch (e) { }
      handleLogout();
      return undefined;
    }

    const timer = setTimeout(() => {
      try { window.alert('Your 24-hour session has expired. You are being logged out.'); } catch (e) { }
      handleLogout();
    }, remaining);

    return () => clearTimeout(timer);
    // }, [user]);
  }, [user, handleLogout]);

  // Synchronize with centralized 30-minute idle session manager
  React.useEffect(() => {
    const onSessionTimeout = () => {
      handleLogout();
    };
    window.addEventListener('app-session-timeout', onSessionTimeout);
    return () => window.removeEventListener('app-session-timeout', onSessionTimeout);
  }, [handleLogout]);

  // Standalone Web App Consent Page (no authentication required)
  if (isWebAppConsentPage) {
    return <WebAppConsent />;
  }

  if (isTermsAndConditionsPage) {
    return <TermsAndConditions onBack={() => {
      try {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = '/webapp-consent';
        }
      } catch (e) { }
    }} />;
  }

  // Standalone Customer Consent Login Page (anonymous access)
  if (isCustomerConsentLoginPage) {
    return (
      <CustomerConsentLogin
        onLoginSuccess={({ email }) => {
          try {
            localStorage.setItem('customer_consent_email', email);
          } catch (e) {
            // ignore storage issues
          }
          window.location.href = '/customer-consent';
        }}
      />
    );
  }

  // Standalone Customer Consent Page (anonymous or redirected access)
  if (isCustomerConsentPage) {
    return <CustomerConsent />;
  }

  if (isDSARDashboardUserPage) {
    return <DSARDashboardUser />;
  }

  if (isLocalSsoLoginPage) {
    return <LocalSsoLogin onLogin={handleLogin} />;
  }

  if (!user || !user?.role || !['admin', 'manager', 'user'].includes(user.role)) {
    // Check if accessing standalone web app consent page
    if (isWebAppConsentPage) {
      return <WebAppConsent />;
    }

    return (
      <>
        <LoadingOverlay />
        <LoginForm onLogin={handleLogin} />
      </>
    );
  }

  if (user.role === 'admin') {
    // Check if accessing standalone web app consent page
    if (isWebAppConsentPage) {
      return <WebAppConsent />;
    }

    return (
      <>
        <LoadingOverlay />
        <BasePage onLogout={handleLogout} onMenu={setPage} user={user}>
          {page === 'tenants' && <TenantsPage token={token} user={user} />}
          {page === 'cookie-consent-template' && <CookieConsentPage tenantId={user.tenant_id} onBack={() => setPage('tenants')} />}
          {page === 'consent-banner-template' && <DpdpConsentPage tenantId={user.tenant_id} onBack={() => setPage('tenants')} />}
          {page === 'dummy-consent' && <DummyConsentPage token={token} user={user} onBack={() => setPage('tenants')} />}
          {page === null && (
            <NotFoundPage
              label="Page not found"
              onHome={() => {
                setPage('tenants');
                try { window.history.replaceState({}, '', '/admin/tenants'); } catch (e) { }
              }}
            />
          )}
        </BasePage>
      </>
    );
  }

  if (user.role === 'manager') {
    // Check if accessing standalone web app consent page
    if (isWebAppConsentPage) {
      return <WebAppConsent />;
    }

    return (
      <>
        <LoadingOverlay />
        <BasePage onLogout={handleLogout} user={user} setManagerPage={setManagerPage}>
          {managerPage === 'cookie-consent-template' ? (
            <CookieConsentPage tenantId={user.tenant_id} onBack={() => setManagerPage('dashboard')} />
          ) : managerPage === 'consent-banner-template' ? (
            <DpdpConsentPage tenantId={user.tenant_id} onBack={() => setManagerPage('dashboard')} />
          ) : managerPage === 'market-consent' ? (
            <MarketConsentPage user={user} />
          ) : managerPage === null ? (
            <NotFoundPage
              label="Page not found"
              onHome={() => {
                setManagerPage('dashboard');
                try { window.history.replaceState({}, '', '/manager'); } catch (e) { }
              }}
            />
          ) : (
            <ManagerPanel token={token} user={user} onLogout={handleLogout} page={managerPage} setPage={setManagerPage} />
          )}
        </BasePage>
      </>
    );
  }

  if (user.role === 'user') {
    // Check if accessing standalone web app consent page
    if (isWebAppConsentPage) {
      return <WebAppConsent />;
    }

    return (
      <>
        <LoadingOverlay />
        <BasePage onLogout={handleLogout} onMenu={handleUserMenu} user={user}>
          {page === 'version' && <VersionPage />}
          {page === 'dashboard' ? (
            <UserDashboard user={user} />
          ) : page === 'user-logs' ? (
            <UserLogsPage
              user={user}
              onMenu={handleUserMenu}
              onBack={() => handleUserMenu('consent')}
            />
          ) : page === 'smart-consent' ? (
            <SmartConsent user={user} />
          ) : page === 'consentoverview' ? (
            <ConsentOverviewPage user={user} onSelect={(r) => { setSelectedConsentOverview(r); setTimeout(() => handleUserMenu('consent'), 0); }} />
          ) : page === 'market-consent' ? (
            <MarketConsentPage user={user} />
          ) : page === 'custom-report' ? (
            <CustomReport user={user} />
          ) : page === 'smart-notices' ? (
            <SmartNotices user={user} onMenu={handleUserMenu} onBack={() => handleUserMenu('notices')} />
          ) : page === 'notices' ? (
            <NoticesPage user={user} onMenu={handleUserMenu} />
          ) : page === 'cookies-notify' ? (
            <CookiesNotifyPage user={user} />
          ) : page === 'consent-banner' ? (
            <ConsentBannerNofity user={user} />
          ) : page === 'dps' ? (
            <DSARDashboard />
          ) : page === 'purpose-consent' || page === 'purpose consent' ? (
            <PurposeConsentPage user={user} onMenu={handleUserMenu} />
          ) : page === null ? (
            <NotFoundPage
              label="Page not found"
              onHome={() => {
                const nextPage = 'consentoverview';
                handleUserMenu(nextPage);
                try { window.history.replaceState({}, '', '/user/consent-overview'); } catch (e) { }
              }}
            />
          ) : (
            <UserPanel user={user} initialTab="consent" initialRoleFilter={selectedConsentOverview} onMenu={handleUserMenu} />
          )}
        </BasePage>
      </>
    );
  }

  return (
    <>
      <LoadingOverlay />
      <LoginForm onLogin={handleLogin} />
    </>
  );
}

export default App;
